/**
 * TabStrip — renders below the AppBar when there's more than one tab
 * open. Main tab is uncloseable and pinned first. Chart tabs are
 * cmd/middle-click-closable from the X icon. Active tab is visually
 * elevated (filled background + accent border).
 *
 * Only renders when tabs.length > 1 so the strip stays out of the way
 * when the user isn't using multi-chart workflows.
 */
import React from 'react';
import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Home';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import CloseIcon from '@mui/icons-material/Close';
import { useTabs } from '../../contexts/TabsContext';
import { useAppTheme } from '../../hooks/useAppTheme';

const TabStrip: React.FC = () => {
  const { tabs, activeTabId, switchTab, closeTab } = useTabs();
  const { C, isDark } = useAppTheme();

  if (tabs.length <= 1) return null;

  return (
    <Box
      role="tablist"
      aria-label="Open chart tabs"
      sx={{
        display: 'flex',
        gap: 0.6,
        alignItems: 'center',
        overflowX: 'auto',
        bgcolor: isDark ? 'rgba(13,17,23,0.96)' : 'rgba(245,247,250,0.96)',
        borderBottom: '1px solid',
        borderColor: 'divider',
        boxShadow: isDark ? '0 2px 6px rgba(0,0,0,0.35)' : '0 2px 6px rgba(0,0,0,0.08)',
        px: 1, py: 0.55,
        position: 'sticky',
        // Stick just BELOW the sticky AppBar (Toolbar 48/52) so the strip stays
        // visible on scroll instead of sliding up behind the header.
        top: { xs: 48, sm: 52 },
        zIndex: 1090, // below AppBar (1100), above page content
        backdropFilter: 'blur(8px)',
      }}
    >
      {tabs.map((t) => {
        const active = t.id === activeTabId;
        const isMain = t.kind === 'main';
        const Icon = isMain ? HomeIcon : CandlestickChartIcon;
        return (
          <Box
            key={t.id}
            role="tab"
            aria-selected={active}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.2, py: 0.45,
              borderRadius: 999,
              cursor: 'pointer',
              fontSize: '0.76rem',
              fontWeight: active ? 700 : 500,
              letterSpacing: 0.2,
              color: active ? C.onSolid : 'text.secondary',
              background: active
                ? `linear-gradient(180deg, ${alpha(C.neutral, 0.95)} 0%, ${C.neutral} 100%)`
                : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)'),
              border: '1px solid',
              borderColor: active ? C.neutral : 'divider',
              boxShadow: active ? `0 1px 8px ${alpha(C.neutral, 0.45)}` : 'none',
              transition: 'all 0.13s ease',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              '&:hover': active ? { filter: 'brightness(1.06)' } : {
                color: 'text.primary',
                background: alpha(C.neutral, isDark ? 0.16 : 0.10),
                borderColor: alpha(C.neutral, 0.5),
              },
            }}
            onClick={() => switchTab(t.id)}
            onAuxClick={(e) => {
              // Middle-click = close
              if (e.button === 1 && !isMain) {
                e.preventDefault();
                closeTab(t.id);
              }
            }}
          >
            <Icon sx={{ fontSize: 14 }} />
            <Box component="span">{t.label}</Box>
            {!isMain && (
              <Tooltip arrow title="Close tab">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); closeTab(t.id); }}
                  sx={{
                    p: 0.1, ml: 0.25,
                    color: active ? 'rgba(255,255,255,0.85)' : 'text.secondary',
                    '&:hover': { color: active ? C.onSolid : C.bear, bgcolor: 'transparent' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default TabStrip;
